import { DESCRIPCION_DE_AGENCIA, IDIOMAS_DE_AGENCIA, PAISES_DE_AGENCIA, SERVICIOS_DE_AGENCIA } from '@mc/socios'
import { BadgeCheck, Check, CheckCircle2, ExternalLink, EyeOff, ImageIcon, Languages, MapPin, Save, Send, Trash2, Upload } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'

import { socio as accionesDeSocio, useRefrescarSocio, useSitios } from '../api/consultas'
import { tarjetaDe, useSubirMedio } from '../api/medios'
import { ErrorDeLaApi } from '../api/nucleo'
import type { AgenciaSocia } from '../api/tipos'
import { Escribenos } from '../componentes/Ayuda'
import { Aviso, Boton, Campo, claseDeEntrada, clases, Selector, Tarjeta } from '../componentes/ui'
import {
  alternar,
  type Borrador,
  borradorDe,
  type CampoDeLaFicha,
  campoDelFallo,
  cuerpoDe,
  dominioDe,
  etiquetasDe,
  extracto,
  hayCambios,
  iniciales,
  limpiar,
  MAXIMO_DE_SERVICIOS,
  problemaDe,
  ubicacionDe,
} from './ficha'

/**
 * La ficha de una agencia socia (CLAUDE.md §30): el formulario con el que pide entrar
 * y con el que la edita después, y la tarjeta con la que sale en el directorio público
 * de agencias de la web comercial. Las reglas son las de `@mc/socios`, las mismas con
 * las que decide el núcleo: el error se dice junto a su campo antes de enviar, y si el
 * núcleo aun así dice que no, se pone junto al campo que él señale.
 */

const mensajeDe = (fallo: unknown, siNo: string) => (fallo instanceof ErrorDeLaApi ? fallo.message : siNo)

const idDe = (campo: CampoDeLaFicha) => `ficha-${campo}`

/** Lleva el foco al campo con el problema y lo pone a la vista. En un grupo de opciones, a su primera opción. */
const enfocar = (campo: CampoDeLaFicha) =>
  requestAnimationFrame(() => {
    const nodo = document.getElementById(idDe(campo))
    if (!nodo) return
    const destino = nodo.matches('input, select, textarea, button') ? nodo : nodo.querySelector<HTMLElement>('button:not(:disabled), input, select, textarea')
    destino?.focus({ preventScroll: true })
    nodo.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })

// --- Piezas del formulario ------------------------------------------------------------------

/**
 * Una parte del formulario: el título y su línea a la izquierda, los campos a la
 * derecha. Por debajo de 768 px de ancho de la tarjeta (no de la pantalla: el menú
 * lateral se come sitio), el título va encima.
 */
const Seccion = ({ titulo, descripcion, children }: { children: ReactNode; descripcion?: ReactNode; titulo: string }) => (
  <section className="grid grid-cols-1 gap-x-10 gap-y-4 border-t border-linea py-6 @3xl:grid-cols-[14rem_minmax(0,1fr)]">
    <div>
      <h3 className="font-display text-sm font-semibold">{titulo}</h3>
      {descripcion && <p className="mt-1 text-[13px] text-tenue">{descripcion}</p>}
    </div>
    <div className="@container flex min-w-0 flex-col gap-5">{children}</div>
  </section>
)

/** Dos campos en fila cuando caben; uno encima de otro en móvil. */
const Pareja = ({ children }: { children: ReactNode }) => <div className="grid grid-cols-1 gap-5 @lg:grid-cols-2">{children}</div>

const Etiqueta = ({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) => (
  <label className="text-[13px] font-medium text-tinta" htmlFor={htmlFor}>
    {children}
  </label>
)

const Nota = ({ error, ayuda }: { ayuda?: ReactNode; error?: string }) =>
  error ? (
    <span className="text-xs text-error" role="alert">
      {error}
    </span>
  ) : ayuda ? (
    <span className="text-xs text-tenue">{ayuda}</span>
  ) : null

/** Opciones de un catálogo que se marcan y desmarcan: los servicios y los idiomas. */
const Chips = ({
  id,
  etiqueta,
  ayuda,
  catalogo,
  elegidos,
  alCambiar,
  maximo = Number.POSITIVE_INFINITY,
  error,
}: {
  alCambiar: (clave: string) => void
  ayuda?: ReactNode
  catalogo: Record<string, { es: string }>
  elegidos: string[]
  error?: string
  etiqueta: string
  id: string
  maximo?: number
}) => {
  const lleno = elegidos.length >= maximo
  return (
    <fieldset aria-invalid={Boolean(error)} className="min-w-0" id={id}>
      <legend className="mb-2 text-[13px] font-medium text-tinta">{etiqueta}</legend>
      <div className="flex flex-wrap gap-2">
        {Object.entries(catalogo).map(([clave, { es }]) => {
          const elegido = elegidos.includes(clave)
          const tope = !elegido && lleno
          return (
            <button
              aria-pressed={elegido}
              className={clases(
                'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors disabled:cursor-not-allowed',
                elegido ? 'border-violeta bg-violeta-suave font-medium text-violeta' : 'border-linea bg-papel text-tinta-2 enabled:hover:border-linea-fuerte enabled:hover:text-tinta',
                tope && 'opacity-50',
                error && !elegido && 'border-error/50',
              )}
              disabled={tope}
              key={clave}
              onClick={() => alCambiar(clave)}
              title={tope ? `Como mucho ${maximo}: desmarca otro para elegir este.` : undefined}
              type="button"
            >
              {elegido && <Check aria-hidden className="-ml-0.5 size-3.5" />}
              {es}
            </button>
          )
        })}
      </div>
      <div className="mt-2">
        <Nota ayuda={ayuda} error={error} />
      </div>
    </fieldset>
  )
}

/** Un interruptor con su explicación al lado. Por debajo es una casilla: se marca con el teclado y lo anuncia el lector. */
const Interruptor = ({ id, titulo, texto, marcado, alCambiar, apagado = false }: { alCambiar: (valor: boolean) => void; apagado?: boolean; id: string; marcado: boolean; texto: ReactNode; titulo: string }) => (
  <label
    className={clases(
      'flex items-start justify-between gap-4 rounded-caja border border-linea bg-papel-2 px-4 py-3.5 transition-colors',
      apagado ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-linea-fuerte',
    )}
    htmlFor={id}
  >
    <span className="min-w-0">
      <span className="block text-sm font-medium text-tinta" id={`${id}-titulo`}>
        {titulo}
      </span>
      <span className="mt-0.5 block text-[13px] text-tenue" id={`${id}-texto`}>
        {texto}
      </span>
    </span>
    <span className="relative mt-0.5 inline-flex shrink-0">
      {/* El nombre es el título; la explicación, su descripción: si no, el lector lee el párrafo entero como nombre. */}
      <input
        aria-describedby={`${id}-texto`}
        aria-labelledby={`${id}-titulo`}
        checked={marcado}
        className="peer sr-only"
        disabled={apagado}
        id={id}
        onChange={(e) => alCambiar(e.target.checked)}
        role="switch"
        type="checkbox"
      />
      <span aria-hidden className="h-5 w-9 rounded-full bg-linea-fuerte transition-colors peer-checked:bg-violeta peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-violeta" />
      <span aria-hidden className="absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow-suave transition-transform peer-checked:translate-x-4" />
    </span>
  </label>
)

const TIPOS_DE_LOGO = ['image/png', 'image/jpeg', 'image/webp', 'image/avif']

/**
 * El logo: una imagen de la biblioteca de medios de la propia cuenta, que se sirve
 * desde nuestro CDN. Se sube a la web más antigua del espacio (la «principal»); si el
 * espacio todavía no tiene ninguna, no hay biblioteca donde guardarlo y se dice.
 */
const SubidaDeLogo = ({ espacio, empresa, logo, alCambiar }: { alCambiar: (logo: Borrador['logo']) => void; empresa: string; espacio: number; logo: Borrador['logo'] }) => {
  const { data: sitios, isPending } = useSitios(espacio)
  const sitio = useMemo(() => (sitios?.length ? sitios.reduce((a, b) => (b.id < a.id ? b : a)) : null), [sitios])
  const subir = useSubirMedio(sitio?.id ?? 0, espacio)
  const [error, setError] = useState<null | string>(null)
  const entrada = useRef<HTMLInputElement>(null)

  const elegir = async (fichero: File | undefined) => {
    if (entrada.current) entrada.current.value = ''
    if (!fichero || !sitio) return
    if (!TIPOS_DE_LOGO.includes(fichero.type)) {
      setError('El logo tiene que ser una imagen PNG, JPG, WebP o AVIF.')
      return
    }
    setError(null)
    try {
      const medio = await subir.mutateAsync({ fichero, alt: `Logo de ${empresa.trim() || 'la agencia'}` })
      alCambiar({ id: medio.id, url: tarjetaDe(medio) })
    } catch (fallo) {
      setError(mensajeDe(fallo, 'No hemos podido subir el logo. Vuelve a intentarlo en un momento.'))
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium text-tinta">Logo</span>
      <div className="flex items-start gap-4">
        <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-caja border border-linea bg-papel-2">
          {logo?.url ? (
            <img alt={`Logo de ${empresa || 'tu agencia'}`} className="size-full object-contain p-2" src={logo.url} />
          ) : logo ? (
            <ImageIcon aria-label="Logo sin vista previa" className="size-6 text-tenue" />
          ) : (
            <span aria-hidden className="font-display text-xl font-semibold text-tenue">
              {empresa.trim() ? iniciales(empresa) : <ImageIcon className="size-6" />}
            </span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {isPending ? (
            <div aria-hidden className="h-8 w-40 animate-pulse rounded-boton bg-papel-3" />
          ) : sitio ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Boton cargando={subir.isPending} icono={<Upload />} onClick={() => entrada.current?.click()} pequeno>
                  {logo ? 'Cambiar el logo' : 'Subir el logo'}
                </Boton>
                {logo && (
                  <Boton disabled={subir.isPending} icono={<Trash2 />} onClick={() => alCambiar(null)} pequeno tipo="peligro">
                    Quitar
                  </Boton>
                )}
              </div>
              <Nota
                ayuda={
                  <>
                    PNG, JPG, WebP o AVIF; cuadrado y con algo de margen se ve mejor. Se guarda en la biblioteca de medios de <span className="font-medium text-tinta-2">{sitio.nombre}</span>.
                  </>
                }
                error={error ?? undefined}
              />
              <input accept={TIPOS_DE_LOGO.join(',')} aria-label="Elegir el logo" className="sr-only" onChange={(e) => void elegir(e.target.files?.[0])} ref={entrada} tabIndex={-1} type="file" />
            </>
          ) : (
            <>
              <p className="text-[13px] text-tinta-2">
                El logo se guarda en la biblioteca de medios de una de tus webs, y todavía no tienes ninguna. Puedes enviar la ficha sin logo y añadirlo cuando la crees.
              </p>
              {logo && (
                <div>
                  <Boton icono={<Trash2 />} onClick={() => alCambiar(null)} pequeno tipo="peligro">
                    Quitar el logo
                  </Boton>
                </div>
              )}
              <Link className="text-[13px] font-medium text-violeta hover:underline" to={`/e/${espacio}/sitios/nuevo`}>
                Crear mi primera web
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// --- El formulario --------------------------------------------------------------------------

const TEXTOS = {
  solicitar: {
    titulo: 'Pide entrar en el programa de agencias',
    descripcion: 'Una persona lee cada ficha. Cuanto más claro digas qué hace tu agencia y para quién, antes te contestamos. Si la aprobamos, esta es la ficha que sale en el directorio de agencias.',
    boton: 'Enviar la solicitud',
    fallo: 'No hemos podido enviar tu solicitud. Vuelve a intentarlo en un momento.',
  },
  solicitada: {
    titulo: 'Tu ficha',
    descripcion: 'Puedes corregirla mientras la revisamos. Si la aprobamos, es la que sale en el directorio de agencias.',
    boton: 'Guardar la ficha',
    fallo: 'No hemos podido guardar tu ficha. Vuelve a intentarlo en un momento.',
  },
  aprobada: {
    titulo: 'Edita tu ficha',
    descripcion: 'Lo que cambies aquí es lo que se ve en el directorio de agencias.',
    boton: 'Guardar la ficha',
    fallo: 'No hemos podido guardar tu ficha. Vuelve a intentarlo en un momento.',
  },
} as const

/**
 * El formulario de la ficha. Sin solicitud (o rechazada) pide entrar (`POST`); en
 * revisión o aprobada, la edita (`PUT`). Quien lo pinte le da una `key` con el estado,
 * para que al cambiar de estado vuelva a leer la ficha del núcleo.
 */
export const FormularioDeFicha = ({ espacio, agencia, pausado }: { agencia: AgenciaSocia | null; espacio: number; pausado: boolean }) => {
  const refrescar = useRefrescarSocio(espacio)
  const acciones = accionesDeSocio(espacio)
  const modo = agencia?.estado === 'aprobada' ? 'aprobada' : agencia?.estado === 'solicitada' ? 'solicitada' : 'solicitar'
  const textos = TEXTOS[modo]
  const edita = modo !== 'solicitar'

  const [guardado, setGuardado] = useState<Borrador>(() => borradorDe(agencia))
  const [borrador, setBorrador] = useState<Borrador>(guardado)
  const [problema, setProblema] = useState<null | { campo: CampoDeLaFicha; mensaje: string }>(null)
  const [tocado, setTocado] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const [hecho, setHecho] = useState(false)

  // Siempre sobre el borrador **vigente**, no sobre el de este pintado: la subida del logo
  // tarda unos segundos y llama aquí al terminar; con `{ ...borrador }` pisaba todo lo que
  // se hubiera escrito mientras subía (correo, teléfono, ciudad, servicios…).
  const cambiar = (parte: Partial<Borrador>) => {
    setBorrador((actual) => ({ ...actual, ...parte }))
    setHecho(false)
  }
  // Después del primer intento, el error se va (o pasa al siguiente campo) según se corrige.
  const primerPintado = useRef(true)
  useEffect(() => {
    if (primerPintado.current) {
      primerPintado.current = false
      return
    }
    if (tocado) setProblema(problemaDe(borrador))
    // Solo cuando cambia el borrador: el error que devuelve el núcleo no se borra solo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrador])
  const errorDe = (campo: CampoDeLaFicha) => (problema?.campo === campo ? problema.mensaje : undefined)
  const largo = borrador.descripcion.trim().length
  const sinCambios = edita && !hayCambios(guardado, borrador)

  const enviar = async () => {
    setTocado(true)
    setFallo(null)
    setHecho(false)
    const encontrado = problemaDe(borrador)
    setProblema(encontrado)
    if (encontrado) {
      enfocar(encontrado.campo)
      return
    }
    const limpio = limpiar(borrador)
    setEnviando(true)
    try {
      await (edita ? acciones.editarAgencia(cuerpoDe(limpio)) : acciones.solicitarAgencia(cuerpoDe(limpio)))
      setBorrador(limpio)
      setGuardado(limpio)
      setTocado(false)
      setHecho(true)
      refrescar()
      // Al pedir entrar, lo siguiente que hay que leer es el aviso de «en revisión», arriba.
      if (!edita) window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      const campo = campoDelFallo(error)
      if (campo) {
        setProblema({ campo, mensaje: mensajeDe(error, textos.fallo) })
        enfocar(campo)
      } else setFallo(mensajeDe(error, textos.fallo))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Tarjeta className="md:p-6">
      <h2 className="font-display text-lg font-semibold">{textos.titulo}</h2>
      <p className="mt-1 max-w-3xl text-[13px] text-tenue">{textos.descripcion}</p>

      <form
        className="@container mt-5"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void enviar()
        }}
      >
        <fieldset className="min-w-0" disabled={pausado}>
          <Seccion descripcion="Cómo se llama y dónde está en Internet." titulo="Tu agencia">
            <Pareja>
              <Campo
                autoComplete="organization"
                error={errorDe('empresa')}
                etiqueta="Empresa"
                id={idDe('empresa')}
                maxLength={120}
                onChange={(e) => cambiar({ empresa: e.target.value })}
                placeholder="Tu agencia S.A.C."
                value={borrador.empresa}
              />
              <Campo
                autoComplete="url"
                error={errorDe('web')}
                etiqueta="Página web"
                id={idDe('web')}
                inputMode="url"
                maxLength={200}
                onChange={(e) => cambiar({ web: e.target.value })}
                placeholder="https://tuagencia.pe"
                value={borrador.web}
              />
            </Pareja>
            <SubidaDeLogo alCambiar={(logo) => cambiar({ logo })} empresa={borrador.empresa} espacio={espacio} logo={borrador.logo} />
          </Seccion>

          <Seccion descripcion="Por aquí te escribimos, y tus futuros clientes si lo quieres." titulo="Contacto">
            <Pareja>
              <Campo
                autoComplete="email"
                ayuda="Tiene que ser del dominio de tu web: así sabemos que la agencia es tuya."
                error={errorDe('correo')}
                etiqueta="Correo de la agencia"
                id={idDe('correo')}
                inputMode="email"
                maxLength={200}
                onChange={(e) => cambiar({ correo: e.target.value })}
                placeholder="hola@tuagencia.pe"
                type="email"
                value={borrador.correo}
              />
              <Campo
                autoComplete="tel"
                ayuda="Con el prefijo del país."
                error={errorDe('telefono')}
                etiqueta="Teléfono de contacto"
                id={idDe('telefono')}
                inputMode="tel"
                maxLength={24}
                onChange={(e) => cambiar({ telefono: e.target.value })}
                placeholder="+51 987 654 321"
                type="tel"
                value={borrador.telefono}
              />
            </Pareja>
            <Pareja>
              <Campo
                autoComplete="address-level2"
                error={errorDe('ciudad')}
                etiqueta="Ciudad"
                id={idDe('ciudad')}
                maxLength={80}
                onChange={(e) => cambiar({ ciudad: e.target.value })}
                placeholder="Lima"
                value={borrador.ciudad}
              />
              <div className="flex flex-col gap-1.5">
                <Etiqueta htmlFor={idDe('pais')}>País</Etiqueta>
                <Selector
                  aria-invalid={Boolean(errorDe('pais'))}
                  autoComplete="country"
                  className={clases(errorDe('pais') && 'border-error!', !borrador.pais && 'text-tenue!')}
                  id={idDe('pais')}
                  onChange={(e) => cambiar({ pais: e.target.value })}
                  value={borrador.pais}
                >
                  <option value="">Elige un país</option>
                  {Object.entries(PAISES_DE_AGENCIA).map(([clave, { es }]) => (
                    <option className="text-tinta" key={clave} value={clave}>
                      {es}
                    </option>
                  ))}
                </Selector>
                <Nota error={errorDe('pais')} />
              </div>
            </Pareja>
          </Seccion>

          <Seccion descripcion="Es por lo que te van a buscar en el directorio." titulo="Qué hacéis">
            <Chips
              alCambiar={(clave) => cambiar({ servicios: alternar(borrador.servicios, clave, SERVICIOS_DE_AGENCIA, MAXIMO_DE_SERVICIOS) })}
              ayuda={`Marca los que hacéis de verdad, hasta ${MAXIMO_DE_SERVICIOS}.`}
              catalogo={SERVICIOS_DE_AGENCIA}
              elegidos={borrador.servicios}
              error={errorDe('servicios')}
              etiqueta="Servicios"
              id={idDe('servicios')}
              maximo={MAXIMO_DE_SERVICIOS}
            />
            <Chips
              alCambiar={(clave) => cambiar({ idiomas: alternar(borrador.idiomas, clave, IDIOMAS_DE_AGENCIA) })}
              ayuda="En los que atendéis a vuestros clientes."
              catalogo={IDIOMAS_DE_AGENCIA}
              elegidos={borrador.idiomas}
              error={errorDe('idiomas')}
              etiqueta="Idiomas"
              id={idDe('idiomas')}
            />
            <div className="flex flex-col gap-1.5">
              <Etiqueta htmlFor={idDe('descripcion')}>Descripción</Etiqueta>
              <textarea
                aria-invalid={Boolean(errorDe('descripcion'))}
                className={clases(claseDeEntrada(errorDe('descripcion')), 'h-auto min-h-36 resize-y py-2 leading-relaxed')}
                id={idDe('descripcion')}
                maxLength={DESCRIPCION_DE_AGENCIA.maximo + 200}
                onChange={(e) => cambiar({ descripcion: e.target.value })}
                placeholder="Qué hacéis, para qué tipo de clientes, cuántas webs montáis al año y qué os diferencia. Es lo que lee quien busca agencia."
                value={borrador.descripcion}
              />
              <span className="flex justify-between gap-3 text-xs">
                <Nota ayuda={`Entre ${DESCRIPCION_DE_AGENCIA.minimo} y ${DESCRIPCION_DE_AGENCIA.maximo.toLocaleString('es')} caracteres.`} error={errorDe('descripcion')} />
                <span className={clases('shrink-0 tabular-nums', largo > DESCRIPCION_DE_AGENCIA.maximo ? 'text-error' : largo >= DESCRIPCION_DE_AGENCIA.minimo ? 'text-ok' : 'text-tenue')}>
                  {largo.toLocaleString('es')} / {DESCRIPCION_DE_AGENCIA.maximo.toLocaleString('es')}
                </span>
              </span>
            </div>
          </Seccion>

          <Seccion descripcion="Tú decides qué se enseña. Lo puedes cambiar cuando quieras." titulo="En el directorio">
            <Interruptor
              alCambiar={(directorio) => cambiar({ directorio })}
              id={idDe('directorio')}
              marcado={borrador.directorio}
              texto={`${modo === 'aprobada' ? 'Tu ficha sale' : 'Cuando te aprobemos, tu ficha sale'} en el directorio público de agencias de Wixy: logo, nombre, ciudad, servicios, idiomas, descripción y un enlace a tu web.`}
              titulo="Quiero aparecer en el directorio de agencias de Wixy"
            />
            <Interruptor
              alCambiar={(contactoPublico) => cambiar({ contactoPublico })}
              apagado={!borrador.directorio}
              id={idDe('contactoPublico')}
              marcado={borrador.contactoPublico && borrador.directorio}
              texto={
                borrador.directorio
                  ? 'Quien vea tu ficha podrá escribirte o llamarte directamente, también por WhatsApp. Si no lo marcas, solo se enseña tu web.'
                  : 'Solo cuenta si apareces en el directorio.'
              }
              titulo="Enseñar mi correo y mi teléfono en el directorio"
            />
          </Seccion>

          <div className="flex flex-col gap-3 border-t border-linea pt-5">
            {fallo && <Aviso tono="error">{fallo}</Aviso>}
            <div className="flex flex-wrap items-center justify-end gap-3">
              {pausado ? (
                <p className="mr-auto text-xs text-tenue">Con el programa pausado no se puede cambiar la ficha.</p>
              ) : hecho ? (
                <p className="mr-auto inline-flex items-center gap-1.5 text-[13px] text-ok" role="status">
                  <CheckCircle2 aria-hidden className="size-4" />
                  {edita ? 'Ficha guardada.' : 'Solicitud enviada.'}
                </p>
              ) : sinCambios ? (
                <p className="mr-auto text-xs text-tenue">No has cambiado nada.</p>
              ) : null}
              <Boton cargando={enviando} disabled={sinCambios} icono={edita ? <Save /> : <Send />} tipo="primario" type="submit">
                {textos.boton}
              </Boton>
            </div>
          </div>
        </fieldset>
      </form>
    </Tarjeta>
  )
}

// --- La tarjeta del directorio ----------------------------------------------------------------

const LogoDeAgencia = ({ empresa, url }: { empresa: string; url?: null | string }) =>
  url ? (
    <span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-caja border border-linea bg-papel">
      <img alt="" className="size-full object-contain p-1.5" src={url} />
    </span>
  ) : (
    <span aria-hidden className="flex size-14 shrink-0 items-center justify-center rounded-caja bg-violeta-suave font-display text-lg font-semibold text-violeta">
      {iniciales(empresa)}
    </span>
  )

/** Cómo sale la agencia en el listado del directorio: logo o iniciales, nombre, dónde está, qué hace y un trozo de quién es. */
export const TarjetaDelDirectorio = ({ agencia }: { agencia: AgenciaSocia }) => {
  const servicios = etiquetasDe(agencia.servicios, SERVICIOS_DE_AGENCIA)
  const idiomas = etiquetasDe(agencia.idiomas, IDIOMAS_DE_AGENCIA)
  const ubicacion = ubicacionDe(agencia.ciudad, agencia.pais)
  const visibles = servicios.slice(0, 4)
  const anio = agencia.resueltaEn ? new Date(agencia.resueltaEn).getUTCFullYear() : null
  const empresa = agencia.empresa ?? ''
  return (
    <article className="flex flex-col rounded-caja border border-linea bg-papel p-5 shadow-media">
      <div className="flex items-center gap-3.5">
        <LogoDeAgencia empresa={empresa} url={agencia.logoUrl} />
        <div className="min-w-0">
          <h3 className="line-clamp-2 font-display text-base leading-tight font-semibold break-words">{empresa || 'Tu agencia'}</h3>
          {ubicacion && (
            <p className="mt-1 flex items-center gap-1 text-[13px] text-tenue">
              <MapPin aria-hidden className="size-3.5 shrink-0" />
              <span className="truncate">{ubicacion}</span>
            </p>
          )}
        </div>
      </div>
      {visibles.length > 0 && (
        <ul aria-label="Servicios" className="mt-4 flex flex-wrap gap-1.5">
          {visibles.map((s) => (
            <li className="rounded-full border border-linea bg-papel-2 px-2.5 py-0.5 text-xs text-tinta-2" key={s}>
              {s}
            </li>
          ))}
          {servicios.length > visibles.length && (
            <li className="rounded-full px-1.5 py-0.5 text-xs text-tenue" title={servicios.slice(visibles.length).join(', ')}>
              +{servicios.length - visibles.length}
            </li>
          )}
        </ul>
      )}
      {agencia.descripcion && <p className="mt-3 text-[13px] leading-relaxed text-tinta-2">{extracto(agencia.descripcion, 170)}</p>}
      {(idiomas.length > 0 || anio) && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-linea pt-3 text-xs text-tenue">
          {idiomas.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Languages aria-hidden className="size-3.5" />
              {idiomas.join(' · ')}
            </span>
          )}
          {anio && (
            <span className="inline-flex items-center gap-1">
              <BadgeCheck aria-hidden className="size-3.5 text-violeta" />
              Socia desde {anio}
            </span>
          )}
        </div>
      )}
    </article>
  )
}

/**
 * La agencia aprobada ve cómo sale en el directorio y va a su página. Si no sale, se
 * le dice por qué: no lo ha marcado, o el programa está pausado.
 */
export const FichaEnElDirectorio = ({ agencia, pausado }: { agencia: AgenciaSocia; pausado: boolean }) => {
  const url = agencia.directorioUrl
  const web = dominioDe(agencia.web)
  return (
    <Tarjeta className="md:p-6">
      <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,23rem)]">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold">Tu ficha en el directorio de agencias</h2>
          <p className="mt-1.5 max-w-xl text-sm text-tenue">
            Así te ve quien busca una agencia en Wixy: tu logo, dónde estás, qué hacéis y en qué idiomas, con un enlace a {web || 'tu web'}.{' '}
            {agencia.contactoPublico ? 'Tu correo y tu teléfono están a la vista en tu página.' : 'Tu correo y tu teléfono no se enseñan.'}
          </p>
          <div className="mt-5">
            {url ? (
              <a
                className="inline-flex h-9 items-center gap-1.5 rounded-boton bg-violeta px-3.5 text-sm font-medium text-white shadow-suave transition-colors hover:bg-violeta-2"
                href={url}
                rel="noopener noreferrer"
                target="_blank"
              >
                Ver mi ficha en el directorio
                <ExternalLink aria-hidden className="size-4" />
              </a>
            ) : (
              <p className="flex max-w-xl items-start gap-2 rounded-campo border border-linea bg-papel-2 px-3 py-2.5 text-[13px] text-tinta-2">
                <EyeOff aria-hidden className="mt-0.5 size-4 shrink-0 text-tenue" />
                <span>
                  {pausado ? (
                    'Con el programa pausado, tu ficha no sale en el directorio.'
                  ) : !agencia.directorio ? (
                    'No sales en el directorio porque no lo has marcado. Márcalo abajo, en «En el directorio», y guarda la ficha.'
                  ) : (
                    <>
                      Tu ficha todavía no tiene su página en el directorio. Si no aparece en un rato, <Escribenos contexto="directorio-de-agencias" />.
                    </>
                  )}
                </span>
              </p>
            )}
          </div>
          <p className="mt-3 text-xs text-tenue">Lo que guardes tarda unos minutos en verse en el directorio.</p>
        </div>
        <div className="min-w-0 rounded-caja border border-linea bg-fondo p-3 sm:p-5">
          <TarjetaDelDirectorio agencia={agencia} />
        </div>
      </div>
    </Tarjeta>
  )
}
